// run-progress-lines.ts
//
// Phase 03 (R-15): pure formatters for the operator-facing progress lines
// `avatar autopilot run` prints while dispatching — a session can otherwise
// run for hours with zero console output. Called from runSupervisorUntilIdle
// (header) and supervisor-loop-attempt-start.ts/supervisor-loop-post-route-
// finalize.ts (▶/■) via `SupervisorTickInput.progress`. No I/O, no Date.now()
// — callers pass already-computed values so this stays trivially unit-testable.
import type { RouteOutcomeResult } from "./supervisor-outcome-router.js";

/** Req 1: the FIRST progress line of a `run`, printed once before any ▶. */
export function formatRunHeaderLine(
  missionId: string,
  ticketCount: number,
  doneCount: number,
  todoCount: number,
): string {
  return (
    `Autopilot run ${missionId} — ${ticketCount} ticket ` +
    `(${doneCount} xong, ${todoCount} chờ). Theo dõi: avatar autopilot status --watch`
  );
}

/** Req 2: printed right after attempt_started is committed (▶ start marker). */
export function formatAttemptStartLine(
  ticketId: string,
  ticketKind: string,
  attemptNo: number,
  now: Date,
): string {
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `▶ ${ticketId} ${ticketKind} · attempt ${attemptNo} · ${hh}:${mm}`;
}

/** Req 2: a short label for the route/verdict — the ■ line's arrow target. */
function shortRouteLabel(route: RouteOutcomeResult): string {
  if (route.kind === "done") return "done";
  if (route.kind === "quota_sleep") return "quota_sleep";
  if (route.kind === "needs_decision") return "needs_decision";
  // retry_decision: "blocked" | "escalate_waiting_human" | a retryable verdict
  // (retry) — decision.outcome carries the short label directly.
  return route.decision.outcome;
}

function formatDurationMmSs(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.round(elapsedMs / 1000));
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const ss = String(totalSeconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

/**
 * Req 2: printed right after attempt_finished is committed (■ end marker).
 * Phase 04 (F1b): `stderrExcerpt` (≤120 chars, already extracted by
 * session-failure-reasons.ts) appends the worker's real failure text when
 * the attempt failed with one — undefined on every other route.
 */
export function formatAttemptEndLine(
  ticketId: string,
  route: RouteOutcomeResult,
  elapsedMs: number,
  stderrExcerpt?: string,
): string {
  const base = `■ ${ticketId} → ${shortRouteLabel(route)} (${formatDurationMmSs(elapsedMs)})`;
  return stderrExcerpt === undefined ? base : `${base} — ${stderrExcerpt}`;
}
